// ShareCarnivalDialog.jsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { Download as DownloadIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const ShareCarnivalDialog = ({
  open,
  onClose,
  shareLink,
  sharedCarnival,
  showSnackbar,
}) => {
  const [shareTabValue, setShareTabValue] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const { t } = useTranslation();

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById("carnival-qr-code");
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");

    let downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `carnival-${sharedCarnival?.title || "share"}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    showSnackbar(t("carnivals.qrCodeDownloaded"), "success");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("carnivals.shareCaravan")}</DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={shareTabValue}
          onChange={(e, newValue) => setShareTabValue(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab label={t("carnivals.copyLink")} />
          <Tab label={t("carnivals.downloadQrCode")} />
        </Tabs>

        {shareTabValue === 0 ? (
          // Link sharing tab
          <>
            <Typography variant="body1" gutterBottom>
              {t("carnivals.shareLinkDescription")}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              value={shareLink}
              InputProps={{
                readOnly: true,
                sx: {
                  bgcolor: "rgba(240, 247, 255, 0.5)",
                  borderRadius: 2,
                },
              }}
              margin="normal"
            />
            <Typography variant="caption" color="text.secondary">
              {t("carnivals.anyoneWithLinkCanView")}
            </Typography>
          </>
        ) : (
          // QR Code tab
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              my: 2,
            }}
          >
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "white",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <QRCodeCanvas
                id="carnival-qr-code"
                value={shareLink}
                size={200}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#1976d2"
              />
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, textAlign: "center" }}
            >
              {t("carnivals.scanQrCode")}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadQR}
              sx={{
                mt: 2,
                borderRadius: 6,
                px: 3,
                borderColor: "#0017E7",
                color: "#0017E7",
                "&:hover": {
                  borderColor: "#0010a3",
                  backgroundColor: "rgba(0, 23, 231, 0.04)",
                },
              }}
            >
              {t("carnivals.downloadQrCode")}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            color: "#64748b",
            "&:hover": {
              backgroundColor: "rgba(100, 116, 139, 0.04)",
            },
          }}
        >
          {t("carnivals.close")}
        </Button>
        {shareTabValue === 0 && (
          <Button
            variant="contained"
            onClick={handleCopyLink}
            sx={{
              borderRadius: 6,
              background: copySuccess
                ? "linear-gradient(45deg, #059669 30%, #10b981 90%)"
                : "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
              "&:hover": {
                background: copySuccess
                  ? "linear-gradient(45deg, #047857 30%, #059669 90%)"
                  : "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {copySuccess ? t("carnivals.copied") : t("carnivals.copyLink")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareCarnivalDialog;
